/* LT-AO-01 — A&O Library list page (master) at /ao-library.
 * Three tabs: Assumptions / Clarifications / Omissions. Each tab is a sortable
 * list with search + show-archived toggle.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, ListChecks, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PageHeader, Card, PrimaryButton, EmptyState, TagChip } from "../components/UI";

const cls = (...a) => a.filter(Boolean).join(" ");

const TABS = [
  ["assumption",    "Assumptions"],
  ["clarification", "Clarifications"],
  ["omission",      "Omissions"],
];

const JOB_TYPE_LABEL = {
  extension: "Extension",
  loft_conversion: "Loft Conversion",
  new_build: "New Build",
  refurb_renovation: "Refurb",
  commercial_fit_out: "Commercial",
  bathroom: "Bathroom",
  kitchen: "Kitchen",
};

export default function AOLibrary() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("assumption");
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/ao-library", { params: { include_archived: showArchived } })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Failed to load A&O library"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items
      .filter((it) => it.type === tab)
      .filter((it) => !needle || (it.statement_text || "").toLowerCase().includes(needle))
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  }, [items, tab, q]);

  const counts = useMemo(() => {
    const m = { assumption: 0, clarification: 0, omission: 0 };
    items.forEach((it) => {
      if (m[it.type] != null) m[it.type] += 1;
    });
    return m;
  }, [items]);

  return (
    <div className="app-container py-8">
      <PageHeader
        title="A&O Library"
        subtitle="Reusable assumption, clarification and omission statements. Tenders seed from this list — promotions from a tender land here."
        actions={<PrimaryButton icon={Plus} onClick={() => navigate(`/ao-library/new?type=${tab}`)}>Add statement</PrimaryButton>}
      />

      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="inline-flex p-0.5 bg-surface-2 rounded-md border border-line">
          {TABS.map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`ao-library-tab-${k}`}
              className={cls(
                "px-3 h-8 rounded transition-colors",
                tab === k
                  ? "bg-surface text-accent shadow-sm"
                  : "text-ink-3 hover:text-ink-2"
              )}
              style={{ fontSize: 12, fontWeight: tab === k ? 600 : 500 }}
            >
              {l} <span className="text-ink-3 ml-1" style={{ fontSize: 11 }}>({counts[k]})</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4" />
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search statements…" spellCheck="true"
              data-testid="ao-library-search"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ fontSize: 13 }}
            />
          </div>
          <label className="inline-flex items-center gap-2 text-ink-3" style={{ fontSize: 12 }}>
            <input type="checkbox" checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="ao-library-show-archived" />
            Show archived
          </label>
        </div>
      </div>

      <Card>
        {loading ? (
          <div className="py-12 text-center text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={ListChecks}
            title={`No ${TABS.find(([k]) => k === tab)[1].toLowerCase()} yet`}
            hint="Click Add statement to create one."
            action={<PrimaryButton icon={Plus} onClick={() => navigate(`/ao-library/new?type=${tab}`)}>Add statement</PrimaryButton>}
          />
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface-2/50">
                <th className="col-head px-4 py-2.5">Statement</th>
                <th className="col-head px-4 py-2.5" style={{ width: 240 }}>Applies to</th>
                <th className="col-head px-4 py-2.5 text-right" style={{ width: 70 }}>Order</th>
                <th className="col-head px-4 py-2.5" style={{ width: 80 }}>Status</th>
                <th style={{ width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id}
                  onClick={() => navigate(`/ao-library/${it.id}`)}
                  data-testid={`ao-library-row-${it.id}`}
                  className="group cursor-pointer hover:bg-surface-2/60 border-b border-line-soft">
                  <td className="px-4 py-3">
                    <span className="text-ink line-clamp-2"
                      style={{ fontSize: 12.5, lineHeight: "1.5" }}>
                      {it.statement_text}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(it.applies_to_job_types || []).slice(0, 4).map((t) => (
                        <TagChip key={t} label={t === "all" ? "All" : (JOB_TYPE_LABEL[t] || t)} tone={t === "all" ? "accent" : "neutral"} />
                      ))}
                      {(it.applies_to_job_types || []).length > 4 && (
                        <span className="text-ink-3" style={{ fontSize: 11 }}>
                          +{it.applies_to_job_types.length - 4}
                        </span>
                      )}
                      {(!it.applies_to_job_types || it.applies_to_job_types.length === 0) && (
                        <span className="text-ink-3 italic" style={{ fontSize: 11 }}>None</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-mono tnum text-ink-3" style={{ fontSize: 11.5 }}>
                      {it.display_order ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {it.is_active === false ? (
                      <span className="text-ink-3" style={{ fontSize: 11 }}>Archived</span>
                    ) : (
                      <span className="text-accent" style={{ fontSize: 11 }}>Active</span>
                    )}
                  </td>
                  <td className="pr-4 py-3 text-right">
                    <ChevronRight size={14} className={cls("text-ink-4 group-hover:text-ink-2")} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
